import api from '../../components/services/apiService';
import Project from '../../common/Utils/project';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { useAuth } from 'oidc-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { useRemediationPlanService } from './useRemediationPlanService';

// 🧩 Interface definitions

export interface RemediationPlanData {
  PlanName: string;
  IsEmergencyRemediationPlan: boolean;
  SendEmailOnCompletion: boolean;
  RemediationPlanStartTime: string; // ISO format date
  IsActive: boolean;
}

export interface WindowsOSUpdate {
  category: string; // e.g., "Updates"
  kbIds: string[];
  selectUpdatesWithoutReboot: boolean;
  operationType: string; // e.g., "Install"
}

export interface RemediationPlanPatch {
  packageIdentifier: string;
  targetVersion?: string;
  blobURL?: string;
  category?: string; // ✅ allow category for dynamic grouping
}

export interface CreateRemediationPlanPayload {
  TenantID: string;
  RemediationPlanData: RemediationPlanData;
  TargetDevice: {
    TargetDeviceFilters: Record<string, any>;
    RemediationPlanAgentList: { TenantAgentID: string }[];
  };
  RemediationPlanUserList: any[];
  RemediationPlanActionList: any[];
  RemediationPlanPatchList: RemediationPlanPatch[];
  WindowsOSUpdateList: WindowsOSUpdate[];
  MacOSUpdateList: any[];
  LinuxOSUpdateList: any[];
}

// 🧠 Hook to create remediation plan
export const useCreateRemediationPlan = () => {
  const { addToast } = useToast();
  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  const { updateRemediationPlanStatus } = useRemediationPlanService();

  const createRemediationPlan = async ({
    remediationPlanPatchList,
    targetDeviceIds,
    remediationPlanData,
  }: {
    remediationPlanPatchList: RemediationPlanPatch[];
    targetDeviceIds: string[];
    remediationPlanData: RemediationPlanData;
  }): Promise<any | undefined> => {
    try {
      if (!auth?.userData?.access_token) {
        addToast('User not authenticated', 'error');
        return;
      }

      const token = auth.userData.access_token;

      // 🧩 Step 1: Separate KB and non-KB patches
      // const kbRegex = /^KB\d+$/i;
      // const kbPatches = remediationPlanPatchList.filter((p) => kbRegex.test(p.packageIdentifier));
      // const nonKbPatches = remediationPlanPatchList.filter((p) => !kbRegex.test(p.packageIdentifier));
      const kbRegex = /^KB\d+$/i;
      const kbPatches = remediationPlanPatchList.filter((patch) => kbRegex.test(patch.PackageIdentifier));
      const nonKbPatches = remediationPlanPatchList
        .filter((patch) => !kbRegex.test(patch.PackageIdentifier))
        .map((patch) => ({
          PackageIdentifier: patch.PackageIdentifier || patch.packageIdentifier || '',
          TargetVersion: patch.TargetVersion || patch.targetVersion || patch.VersionToBeInstalled || '',
          IsUpdateonly: patch.IsUpdateonly !== undefined ? patch.IsUpdateonly : true,
          IsInstallonly: patch.IsInstallonly !== undefined ? patch.IsInstallonly : false,
        }));

      // 🧩 Step 2: Group KB patches by category (if available)
      const groupedKBs = kbPatches.reduce<Record<string, string[]>>((acc, patch) => {
        const cat = patch.category || 'Updates'; // ✅ dynamic category
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(patch.PackageIdentifier);
        return acc;
      }, {});

      // 🧩 Step 3: Build WindowsOSUpdateList dynamically
      const windowsOSUpdateList: WindowsOSUpdate[] = Object.entries(groupedKBs).map(([category, kbIds]) => ({
        category,
        kbIds,
        selectUpdatesWithoutReboot: false,
        operationType: 'Install',
      }));

      // 🧩 Step 4: Build TargetDevice list
      const targetDevice = {
        TargetDeviceFilters: {}, // empty filters
        RemediationPlanAgentList: targetDeviceIds.map((agentId) => ({
          TenantAgentID: agentId,
        })),
      };

      // 🧩 Step 5: Build payload
      const payload: CreateRemediationPlanPayload = {
        TenantID: selectedTenantId || '',
        RemediationPlanData: remediationPlanData,
        TargetDevice: targetDevice,
        RemediationPlanUserList: [],
        RemediationPlanActionList: [],
        RemediationPlanPatchList: nonKbPatches,
        WindowsOSUpdateList: windowsOSUpdateList,
        MacOSUpdateList: [],
        LinuxOSUpdateList: [],
      };

      // 🧩 Step 6: API call
      const response = await api.post(`${Project.api}patchcenterservice/remediationplan/add-remediation-plan`, JSON.stringify(payload));

      await updateRemediationPlanStatus(selectedTenantId as string, response.data.data, 'Approved');

      if (response && response.data) {
        addToast('Remediation plan created successfully', 'success', 4000);
        return response.data;
      }
    } catch (error) {
      console.error('Error creating remediation plan:', error);
      addToast('Failed to create remediation plan', 'error', 4000);
    }
  };

  return { createRemediationPlan };
};
